if (!customElements.get("color-dropdowns")) {
  customElements.define(
    "color-dropdowns",
    class ColorDropdowns extends HTMLElement {
      variantNameChangedUnsubscriber = undefined;

      constructor() {
        super();

        this.colorDropdownContainers = Array.from(
          this.getElementsByClassName("color-dropdown-container")
        );

        this.colorDropdownContainers.forEach(cdc => {
          const selectedColor = cdc.querySelector(".selected-color");
          const colorsContainers = cdc.querySelectorAll(".colors-container");

          // Toggle color dropdown open/closed
          selectedColor.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            colorsContainers.forEach(csc => {
              if (csc.dataset.colorsetName === selectedColor.dataset.currentColorset) {
                csc.classList.toggle("hidden");
              }
            });
          });

          colorsContainers.forEach(csc => {
            csc.getElementsByClassName("color-button") &&
            Array.from(csc.getElementsByClassName("color-button")).forEach(color => {
              color.addEventListener("click", () => {
                selectedColor.querySelector("p").innerHTML = color.dataset.colorName;
                selectedColor.querySelector("input").value = color.dataset.colorName;
                selectedColor.querySelector("img").src = color.dataset.imageSrc;
                selectedColor.style.outline = "";
                csc.classList.add("hidden");

                const input = selectedColor.querySelector("input");
                input.value = color.dataset.colorName;
                input.dataset.storedValue = color.dataset.colorName;

                this._renderColorPreview();
              });
            });
          });

          // Colorset switcher
          const colorsetOptionsContainer = cdc.querySelector(".colorset-options-container");
          if (colorsetOptionsContainer) {
            const colorsetOptions = Array.from(
              colorsetOptionsContainer.querySelectorAll(".colorset-option")
            );

            this._setColorsetBorder(colorsetOptions[0], true);

            colorsetOptions.forEach(cso => {
              cso.addEventListener("click", () => {
                selectedColor.dataset.currentColorset = cso.dataset.colorsetName;

                colorsetOptions.forEach(opt => {
                  this._setColorsetBorder(opt, opt === cso);
                });

                colorsContainers.forEach(csc => {
                  csc.classList.add("hidden");
                  if (csc.dataset.colorsetName === cso.dataset.colorsetName) {
                    selectedColor.querySelector("p").innerHTML = "Please select a color";
                    selectedColor.querySelector("input").value = "";
                    selectedColor.querySelector("img").src =
                      "https://cdn.shopify.com/s/files/1/0631/2889/5599/files/White.png?v=1743806970";
                    
                    const input = selectedColor.querySelector("input");
                    input.value = "";
                    input.dataset.storedValue = "";

                    this._renderColorPreview();
                  }
                });
              });
            });
          }
        });

        this._initStoredValues();
        this._attachFormValidation();
      }

      _setColorsetBorder(el, active) {
        el.style.setProperty("--border-width", active ? "2px" : "1px");
        el.style.setProperty("--border-color", active ? "black" : "rgba(0, 0, 0, 0.26)");
      }

      _hideContainer(cdc) {
        const input = cdc.querySelector("input");
        input.dataset.storedValue = input.value;
        input.name = input.dataset.storedName;
        input.dataset.storedColorset = input.dataset.storedColorset || "";
        input.value = "";
        input.name = "";
        cdc.classList.add("hidden");
      }

      _showContainer(cdc) {
        const input = cdc.querySelector("input");
        if (!input) return;
        
        input.value = input.dataset.storedValue || "";
        input.name = input.dataset.storedName;
        
        cdc.classList.remove("hidden");
      }

      _initStoredValues() {
        this.colorDropdownContainers.forEach(cdc => {
          const input = cdc.querySelector("input");
          if (input && input.dataset.storedValue === undefined) {
            input.dataset.storedValue = input.value || "";
          }
          if (cdc.classList.contains("hidden")) {
            input.name = "";
          } else {
            input.name = input.dataset.storedName;
          }
        });
      }

      _attachFormValidation() {
        const form = this.closest('form') || document.querySelector('form[action="/cart/add"]');
        if (!form) return;
      
        // Use capture: true to run BEFORE the theme's Ajax handler
        form.addEventListener("submit", (e) => {
          if (!this._validateColors()) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        }, { capture: true });
      
        // Also intercept the add-to-cart button click directly as a fallback
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) {
          submitBtn.addEventListener("click", (e) => {
            if (!this._validateColors()) {
              e.preventDefault();
              e.stopImmediatePropagation();
            }
          }, { capture: true });
        }
      }

      _validateColors() {
        const emptyFields = this.colorDropdownContainers.filter(cdc => {
          if (cdc.classList.contains("hidden")) return false;
          const input = cdc.querySelector("input");
          return !input || !input.value.trim();
        });
      
        if (emptyFields.length > 0) {
          emptyFields.forEach(cdc => {
            const selectedColor = cdc.querySelector(".selected-color");
            if (selectedColor) selectedColor.style.outline = "2px solid red";
          });
          emptyFields[0].scrollIntoView({ behavior: "smooth", block: "center" });
          return false;
        }
        return true;
      }

      // ─── Color Preview ──────────────────────────────────────────────────────────
      _getVisibleLocations() {
        // Returns ordered list of visible color locations regardless of selection state
        const order = ["Fill Color", "Overlay Color", "Color", "3D Color", "Inner Outline Color", "Outline Color", "Outer Outline Color"];
        const locations = [];

        this.colorDropdownContainers.forEach(cdc => {
          if (cdc.classList.contains("hidden")) return;
          if (cdc.querySelector('input').value === "No Outline") return;
          locations.push(cdc.dataset.colorLocation);
        });

        locations.sort((a, b) => {
          const ai = order.indexOf(a);
          const bi = order.indexOf(b);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });

        return locations;
      }

      _getSelectionMap() {
        // Returns { location -> { colorName, imageSrc } } for selected inputs only
        const map = {};
        this.colorDropdownContainers.forEach(cdc => {
          if (cdc.classList.contains("hidden")) return;
          const input = cdc.querySelector("input");
          if (!input || !input.value.trim()) return;
          const img = cdc.querySelector(".selected-color img");
          map[cdc.dataset.colorLocation] = {
            colorName: input.value,
            imageSrc: img ? img.src : ""
          };
        });
        return map;
      }

      _renderColorPreview() {
        const stack = this.querySelector(".dn-color-preview__swatch-stack");
        const labels = this.querySelector(".dn-color-preview__labels");
        if (!stack || !labels) return;

        const locations = this._getVisibleLocations(); // structure from variant
        const selections = this._getSelectionMap();    // actual picks
        const total = locations.length;

        const maxSize = 64;
        const minSize = 36;
        const step = total > 1 ? (maxSize - minSize) / (total - 1) : 0;

        const PLACEHOLDER_SRC = "https://cdn.shopify.com/s/files/1/0631/2889/5599/files/White.png?v=1743806970";

        stack.innerHTML = "";
        labels.innerHTML = "";

        if (total === 0) return;

        // Render layers outside-in (last location = outermost ring)
        [...locations].reverse().forEach((location, i) => {
          const size = maxSize - (i * step);
          const selected = selections[location];

          const layer = document.createElement("div");
          layer.className = "dn-preview-layer";
          if (!selected) layer.classList.add("dn-preview-layer--empty");
          layer.style.width = `${Math.round(size)}px`;
          layer.style.height = `${Math.round(size)}px`;

          const img = document.createElement("img");
          img.src = selected ? selected.imageSrc : PLACEHOLDER_SRC;
          img.alt = selected ? selected.colorName : location;
          layer.appendChild(img);
          stack.appendChild(layer);
        });

        // Render labels fill-first
        locations.forEach(location => {
          const selected = selections[location];

          const row = document.createElement("div");
          row.className = "dn-preview-label";
          if (!selected) row.classList.add("dn-preview-label--empty");

          const dot = document.createElement("div");
          dot.className = "dn-preview-label__dot";
          if (!selected) dot.classList.add("dn-preview-label__dot--empty");
          const dotImg = document.createElement("img");
          dotImg.src = selected ? selected.imageSrc : PLACEHOLDER_SRC;
          dotImg.alt = "";
          dot.appendChild(dotImg);

          const name = document.createElement("span");
          name.className = "dn-preview-label__name";
          name.textContent = `${location}:`;

          const val = document.createElement("span");
          val.className = "dn-preview-label__value";
          val.textContent = selected ? selected.colorName : "—";

          row.appendChild(dot);
          row.appendChild(name);
          row.appendChild(val);
          labels.appendChild(row);
        });
      }



      // ─── Lifecycle ──────────────────────────────────────────────────────────

      get sectionId() {
        return this.getAttribute("data-section-id");
      }

      applyInitialVisibility() {
        const variantData = JSON.parse(document.querySelector("script[data-selected-variant]").innerText);
        if (variantData != undefined){
          this.updateColorDropdownsOnVariantChange(variantData.name);
          this.setDefaultColors();
        } else {
          this.updateColorDropdownsOnVariantChange("Outline");
          this.setDefaultColors();
        }
      }

      connectedCallback() {
        this.applyInitialVisibility();
        this.variantNameChangedUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            this.variantName = event.data.variant.name.toString();
            this.updateColorDropdownsOnVariantChange(this.variantName);
          }
        );
      }

      disconnectedCallback() {
        this.variantNameChangedUnsubscriber?.();
      }

      // ─── Variant Logic ──────────────────────────────────────────────────────

      setDefaultColors(options) {
        JSON.parse(document.getElementById("dn-default-color-options").innerText).defaultColors.forEach(
          e => {
            if(e.value != null) {
              let cdc = document.querySelector(`.color-dropdown-container[data-color-location="${e.location}"]`);
              if(e.location == 'Color'){
                cdc = cdc === null ? document.querySelector(`.color-dropdown-container[data-color-location="Fill Color"]`) : cdc;
              }
              if(cdc != null){
                console.log(e);
                
                const sc = cdc.querySelector('.selected-color'); 
                sc.querySelector("p").innerHTML = e.value.color_name;
                sc.querySelector("input").value = e.value.color_name;
                sc.querySelector("img").src = e.value.image_url;

                const input = sc.querySelector("input");
                input.value = e.value.color_name;
                input.dataset.storedValue = e.value.color_name;

                this._renderColorPreview();
              }
            }
          }
        )
      }

      updateColorDropdownsOnVariantChange(variantName) {
        
        const variantOptions = variantName.split(" / ");
        const isSolid =
          variantName.includes("Solid") &&
          !(!variantName.includes("Solid Color") && variantName.includes("Solid Fill Color"));
        const is3DOnly = variantName.includes("3D Only");
        const isSpecial = variantName.includes("Model Matched Pattern");
        const isSingleOutline = variantOptions.includes("Single Outline");
        const is3DChrome = variantName.includes("Chrome") && variantName.includes("3D");
        const is3DOverlay = variantName.includes("Domed Overlay") && variantName.includes("3D");
        
        // Misc ───────────
        const isDomed = !variantName.split("-").at(-1).includes("Flat");
        const domedWord = document.querySelector(".product__title span[data-word=Domed]");
        if(!isDomed && domedWord != undefined){
          domedWord.classList.add("hidden");
          domedWord.classList.remove("word");
        } else if (isDomed && domedWord != undefined) {
          domedWord.classList.remove("hidden");
          domedWord.classList.add("word");
        }
        
        let flatButton = document.querySelector("fieldset label[title='Flat']");
        flatButton = flatButton === null ? document.querySelector("fieldset label[title='Flat Vinyl']") : flatButton;
        if(flatButton != null){
          const flatText = Array.from(flatButton.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
          if(flatText != null){
            flatText[0].data = "Flat Vinyl";
            //flatButton.innerhtml = "Flat Vinyl";
          }
        }
        // end Misc ───────
        
        this.colorDropdownContainers.forEach(cdc => {
          const location = cdc.dataset.colorLocation;
          
          if (isSpecial) {
            this._hideContainer(cdc);
            return;
          }
          
          if (location === "Color") {
            this._showContainer(cdc);
            return;
          }
          
          if (location === "3D Color") {
            is3DChrome ? this._hideContainer(cdc) : this._showContainer(cdc);
            if (is3DOverlay) {
              document.getElementById("colorset-dn-3d-specialties").classList.add('hidden');
              document.getElementById('3D Color-domed-numbers-3d').click()
            } else {
              document.getElementById("colorset-dn-3d-specialties").classList.remove('hidden');
            }
            return;
          }
          
          if (location === "Overlay Color") {
            is3DOnly ? this._hideContainer(cdc) : this._showContainer(cdc);
            return;
          }
          
          if (location === "Inner Outline Color") {
            if (isSingleOutline) {
              this._showContainer(cdc);
              cdc.querySelector("p").innerHTML = "Outline Color";
              cdc.querySelector("input").name = "properties[Outline Color]";
            } else if (isSolid) {
              this._hideContainer(cdc);
            } else {
              this._showContainer(cdc);
              //cdc.querySelector("input").value = "";
              cdc.querySelector("p").innerHTML = "Inner Outline Color";
              cdc.querySelector("input").name = "properties[Inner Outline Color]";
            }
            return;
          }
          
          if (location === "Outer Outline Color") {
            isSingleOutline || isSolid
            ? this._hideContainer(cdc)
            : this._showContainer(cdc);
            return;
          }
          
          // All other locations
          isSolid || is3DOnly ? this._hideContainer(cdc) : this._showContainer(cdc);
        });
        this._renderColorPreview();
      }
    }
  );
}